import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { loadingStepsFor, type ScanContext } from '@/data/clusterscope-data';

const STEP_INTERVAL_MS = 700;

interface LoadingScreenProps {
  scanContext: ScanContext;
  onComplete: () => void;
}

export default function LoadingScreen({ scanContext, onComplete }: LoadingScreenProps) {
  const steps = loadingStepsFor(scanContext.mode, scanContext.label);
  const [completedCount, setCompletedCount] = useState(0);

  useEffect(() => {
    setCompletedCount(0);
  }, [scanContext.mode, scanContext.label]);

  useEffect(() => {
    if (completedCount >= steps.length) {
      const timeout = setTimeout(onComplete, 500);
      return () => clearTimeout(timeout);
    }
    const timeout = setTimeout(() => setCompletedCount((c) => c + 1), STEP_INTERVAL_MS);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedCount, steps.length]);

  return (
    <div className="relative flex min-h-screen w-full flex-col items-center justify-center overflow-hidden bg-background px-4">
      <div className="pointer-events-none absolute inset-0 bg-grid-pattern opacity-[0.25]" />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary opacity-[0.08] blur-[120px]" />

      <div className="relative z-10 flex flex-col items-center">
        <div className="relative mb-12 h-16 w-16">
          <div className="absolute inset-0 rounded-full border-[3px] border-white/[0.08]" />
          <motion.div
            className="absolute inset-0 rounded-full border-[3px] border-transparent"
            style={{
              borderTopColor: 'hsl(var(--primary))',
              borderRightColor: 'hsl(var(--primary) / 0.35)',
            }}
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1.1, ease: 'linear' }}
          />
        </div>

        <div className="flex flex-col gap-3.5">
          {steps.map((step, i) => {
            const done = i < completedCount;
            const active = i === completedCount;
            return (
              <div key={step} className="flex items-center gap-3">
                <span className="relative flex h-4 w-4 items-center justify-center">
                  <motion.span
                    className="absolute inset-0 rounded-full"
                    animate={{
                      backgroundColor: done ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground) / 0.18)',
                      boxShadow: done
                        ? '0 0 10px 2px hsl(var(--primary) / 0.55)'
                        : '0 0 0px 0px transparent',
                    }}
                    transition={{ duration: 0.35 }}
                  />
                  {done && <Check className="relative h-2.5 w-2.5 text-background" strokeWidth={3} />}
                  {active && !done && (
                    <motion.span
                      className="absolute inset-0 rounded-full border border-primary/50"
                      animate={{ scale: [1, 1.6], opacity: [0.6, 0] }}
                      transition={{ repeat: Infinity, duration: 1 }}
                    />
                  )}
                </span>
                <span
                  className={`text-sm transition-colors duration-300 ${
                    done ? 'text-primary' : active ? 'text-foreground/80' : 'text-muted-foreground/50'
                  }`}
                >
                  {step}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
